/**
 * Timeline audio mix for in-browser export, placed like server `_export_audio_pieces_direct`
 * (editor time == output time) and rendered in fixed windows so a long export never holds
 * the whole mix in memory.
 */

export const MIX_SAMPLE_RATE = 48000;
export const MIX_CHANNELS = 2;

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Output-timeline pieces for audible clips, clipped to `totalSeconds`. */
export function audioPiecesForExport(audioClips, totalSeconds) {
  const total = Math.max(0.01, num(totalSeconds, 0.01));
  const pieces = [];
  for (const ac of audioClips || []) {
    if (!ac || ac.muted) continue;
    const url = String(ac.audio_url || '').trim();
    if (!url || url.startsWith('blob:')) continue;
    const a0 = Math.max(0, num(ac.start_time));
    const p = Math.max(0.01, num(ac.playback_seconds, 0.01));
    const a1 = Math.min(a0 + p, total);
    if (a1 - a0 < 1e-3) continue;
    pieces.push({
      url,
      start: a0,
      end: a1,
      trimStart: Math.max(0, num(ac.trim_start)),
      volume: Math.max(0, Math.min(3, ac.volume == null ? 0.8 : num(ac.volume, 0.8))),
      speed: Math.max(0.25, Math.min(4, num(ac.audio_playback_speed, 1) || 1)),
    });
  }
  return pieces;
}

/**
 * Renders the mix window by window. `decode(url)` must resolve an AudioBuffer.
 * `renderUntil(t)` yields every not-yet-rendered window that starts before `t`.
 */
export class ChunkedAudioMixer {
  constructor(pieces, totalSeconds, decode, { windowSeconds = 30 } = {}) {
    this.pieces = pieces;
    this.total = Math.max(0.01, totalSeconds);
    this.decode = decode;
    this.window = windowSeconds;
    this.next = 0;
  }

  get done() {
    return this.next >= this.total - 1e-6;
  }

  async renderWindow(c0, c1) {
    const frames = Math.max(1, Math.round((c1 - c0) * MIX_SAMPLE_RATE));
    const ctx = new OfflineAudioContext(MIX_CHANNELS, frames, MIX_SAMPLE_RATE);
    for (const pc of this.pieces) {
      const lo = Math.max(pc.start, c0);
      const hi = Math.min(pc.end, c1);
      if (hi - lo < 1e-4) continue;
      const buffer = await this.decode(pc.url);
      if (!buffer) continue;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value = pc.speed;
      const gain = ctx.createGain();
      gain.gain.value = pc.volume;
      src.connect(gain).connect(ctx.destination);
      const offset = pc.trimStart + (lo - pc.start) * pc.speed;
      if (offset >= buffer.duration) continue;
      src.start(lo - c0, offset, (hi - lo) * pc.speed);
    }
    return ctx.startRendering();
  }

  async *renderUntil(t) {
    while (!this.done && this.next < t) {
      const c0 = this.next;
      const c1 = Math.min(this.total, c0 + this.window);
      this.next = c1;
      yield await this.renderWindow(c0, c1);
    }
  }
}

/** Decode each audio URL once (shared across windows). */
export function createAudioDecoder(cache) {
  const decoded = new Map();
  let ctx = null;
  return (url) => {
    if (!decoded.has(url)) {
      const p = (async () => {
        ctx = ctx || new OfflineAudioContext(MIX_CHANNELS, 1, MIX_SAMPLE_RATE);
        const blob = await cache.blob(url);
        cache.releaseBlob(url);
        return ctx.decodeAudioData(await blob.arrayBuffer());
      })().catch(() => null);
      decoded.set(url, p);
    }
    return decoded.get(url);
  };
}
